import store from "../store";
import * as actions from "@/store/action-types";
import apiConstants from "@/apiConstants.js";

export const userData = () => store.state.auth && store.state.auth.user;
export const userOrganisation = () =>
  store.state.auth && store.state.auth.organisation;
export const userOrganisations = () =>
  store.state.auth && store.state.auth.organisations;

export const pendingUser = () =>
  userData() && canOnlyUpdatePassword() && userData().user;

export const currentUser = () => userData() && userData().user;

export const authToken = () => {
  return userData() && userData().auth_token;
};

export const userPermissions = () =>
  userOrganisation() && userOrganisation().permissions;

export const userCan = (group, action, activationRequired = false) => {
  const permission =
    userPermissions() &&
    !!(userPermissions()[group] && userPermissions()[group][action]);
  return activationRequired ? permission && isOrganisationActive() : permission;
};

export const isProductAccessible = product => {
  const { features } = userOrganisation();
  return features && features[product] && features[product].is_accessible;
};

export const isOrganisationOwner = () => {
  return userOrganisation() && userOrganisation().is_owner;
};

export const isUserActive = () => {
  const active = apiConstants.GLOBAL_STATUS.ACTIVE;
  const user = currentUser();
  return user && user.status_id === active;
};

export const isOrganisation = () => {
  return !!(userOrganisation() && userOrganisation().summary);
};

export const isOrganisationActive = () => {
  const active = apiConstants.GLOBAL_STATUS.ACTIVE;
  const organisation = userOrganisation().summary
    ? userOrganisation().summary
    : {};
  return organisation && organisation.status_id === active;
};

export const isAccountAwaitingFullAccess = () => {
  const fullAccess = apiConstants.GLOBAL_STATUS.AWAITING_FULL_ACCESS;
  const user = currentUser();
  return user && user.status_id === fullAccess;
};

export const isUserVerified = () => {
  const verified = apiConstants.GLOBAL_STATUS.VERIFIED;
  const user = currentUser();
  return user && user.status_id === verified;
};

export const isOrganisationVerified = () => {
  const verified = apiConstants.GLOBAL_STATUS.VERIFIED;
  const organisation = userOrganisation() && userOrganisation().summary;
  return organisation && organisation.status_id === verified;
};

export const logout = () => {
  localStorage.clear();
  sessionStorage.clear();

  store.dispatch(actions.ORGANISATION, {});
  store.dispatch(actions.ORGANISATIONS, []);
  store.dispatch(actions.WALLET, {});
  store.dispatch(actions.USER, {});
};

export const canOnlyUpdatePassword = () => {
  const permissions = userPermissions();
  const authPermissions =
    permissions && permissions.auth && Object.keys(permissions.auth);
  return authPermissions === 1 && permissions.auth["localauth.updatepassword"];
};

export const isInvalidAuth = data => {
  const accessRelatedCodes = ["INVALID_CRED", "NO_AUTH"];
  return typeof data === "string" && accessRelatedCodes.includes(data);
};

export const isAccessDenied = data => {
  const accessRelatedCodes = ["INVALID_CRED", "ACCESS_DENIED", "NO_AUTH"];
  if (typeof data === "string" && accessRelatedCodes.includes(data)) {
    logout();
    return true;
  }
  return false;
};

/**
 * Returns title case
 * @param {String} str
 * @returns {String}
 */
export const titleCase = str => {
  const title = str.replace(/_/g, " ");
  return title
    .split(" ")
    .map(sAry => sAry.charAt(0).toUpperCase() + sAry.slice(1))
    .join(" ");
};

/**
 * Return boolean based on date comparisions
 * @param {Object} startDate Object
 * @param {Object} endDate Object
 * @returns {Boolean}
 */
export const dateRangeValidation = (startDate, endDate) => {
  return startDate < endDate;
};

/**
 * Returns sum of elements of an array
 * @param {Array} arr Array
 * @returns {Number}
 */
export const sumOfElements = arr => {
  return arr.reduce((accumulator, element) => accumulator + element, 0);
};

/**
 * Return field name with order by/ascending
 * @param {String} field Object
 * @param {Number} sort Object
 * @returns {String}
 */
export const orderBy = (field, sort = 0) => {
  return `&order_by=${field}&is_asc=${sort}`;
};

/**
 * Resends OTP
 * @param {Object} reqClient Object
 * @param {Object} body Object
 * @returns {Object}
 */
export const resendOtp = (reqClient, body) => {
  return reqClient
    .post(
      "resend",
      {
        body: body,
        method: "POST"
      },
      {
        headers: {
          "data-baseurl-namespace": apiConstants.URLS.OTP
        }
      }
    )
    .then(response => {
      const { data } = response;
      if (data && !data.error) {
        return data;
      } else {
        return data;
      }
    })
    .catch(err => {
      return err;
    });
};

/**
 * Load Organisations
 */
export const getUserOrganisations = reqClient => {
  const userId = userData().user && userData().user.id;
  const apiURL = `accounts/users/${userId}/organisations`;
  const params = `&embeds=["status"]&filters=[{"field":"organisation.status_id","value":"3","cond_op":"not like"}]`;

  return new Promise((resolve, reject) => {
    reqClient
      .post(apiURL, {
        method: "GET",
        params
      })
      .then(response => {
        const { data } = response;
        store.dispatch(actions.ORGANISATIONS, data.list);
        resolve(response);
      })
      .catch(err => {
        reject(err);
      });
  });
};

/**
 * Load Active Organisation Wallet
 */
export const getActiveOrganisationWallet = reqClient => {
  return new Promise((resolve, reject) => {
    reqClient
      .post(
        "wallets",
        {
          method: "GET"
        },
        {
          headers: {
            "data-baseurl-namespace": apiConstants.URLS.PAYMENT
          }
        }
      )
      .then(response => {
        const { data } = response;

        if (data.list.length) {
          store.dispatch(actions.WALLET, data.list[0]);
        }

        resolve(response);
      })
      .catch(err => {
        reject(err);
      });
  });
};

/**
 * Verification Types
 */
export const verificationTypes = reqClient => {
  return reqClient
    .post(
      "verifications/types",
      {
        method: "GET"
      },
      {
        headers: {
          "data-baseurl-namespace": apiConstants.URLS.AUTHX
        }
      }
    )
    .then(response => {
      const { data } = response;
      return data;
    })
    .catch(err => {
      return err;
    });
};

/**
 * Field Types
 */
export const fieldTypes = (reqClient, sessionToken = null) => {
  const headers = {
    "data-baseurl-namespace": apiConstants.URLS.AUTHX
  };
  if (!currentUser() && sessionToken) {
    headers["Session-Token"] = sessionToken;
  }

  return reqClient
    .post(
      "fields/types",
      {
        method: "GET"
      },
      {
        headers: headers
      }
    )
    .then(response => {
      const { data } = response;
      return data;
    })
    .catch(err => {
      return err;
    });
};

/**
 * Redirect to social login
 * @param {Object} reqClient Object
 * @param {String} type String
 * @param {String} sessionToken Object
 * @returns {Object}
 */
export const redirectToSocialLogin = (reqClient, type, sessionToken) => {
  const apiUrl = `apps/social/${type}/auth`;

  return reqClient
    .post(
      apiUrl,
      {
        method: "GET"
      },
      {
        headers: {
          "Session-Token": sessionToken
        }
      }
    )
    .then(response => {
      const { data } = response;
      return data;
    })
    .catch(err => {
      return err;
    });
};
